import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";
import { fmtMoney } from "../lib/exports";
import { Plus, Save, Trash2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function CapturaGastos() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const puedeEditar = canEdit(user, "captura_gastos");
  const [fecha, setFecha] = useState(today());
  const [sucursales, setSucursales] = useState([]);
  const [sucursalId, setSucursalId] = useState("");
  const [categorias, setCategorias] = useState([]);
  const [filas, setFilas] = useState([{ categoria: "", importe: "", comentario: "", metodo_pago: "efectivo" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/sucursales"), api.get("/gastos/categorias")]).then(([rs, rc]) => {
      setSucursales(rs.data);
      setCategorias(rc.data);
      if (rs.data.length) setSucursalId(user?.sucursal_default || rs.data[0].id);
    });
  }, [user]);

  const setFila = (i, field, val) => {
    setFilas((prev) => prev.map((f, idx) => (idx === i ? { ...f, [field]: val } : f)));
  };
  const addFila = () => setFilas((prev) => [...prev, { categoria: "", importe: "", comentario: "", metodo_pago: "efectivo" }]);
  const removeFila = (i) => setFilas((prev) => prev.filter((_, idx) => idx !== i));

  const total = filas.reduce((s, f) => s + (parseFloat(f.importe) || 0), 0);

  const guardar = async () => {
    if (!sucursalId) return toast.error("Selecciona sucursal");
    const validos = filas.filter((f) => f.categoria && parseFloat(f.importe) > 0);
    if (!validos.length) return toast.error("Agrega al menos un gasto válido");
    setSaving(true);
    try {
      for (const f of validos) {
        await api.post("/gastos", {
          fecha,
          sucursal_id: sucursalId,
          categoria: f.categoria,
          importe: parseFloat(f.importe),
          comentario: f.comentario || "",
          metodo_pago: isAdmin ? (f.metodo_pago || "efectivo") : "efectivo",
          tipo: "diario",
        });
      }
      toast.success(`${validos.length} gasto(s) guardado(s)`);
      setFilas([{ categoria: "", importe: "", comentario: "", metodo_pago: "efectivo" }]);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Operación</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Captura de Gastos</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isAdmin
            ? "Registra los gastos del día por categoría y método de pago."
            : "Registra los gastos del día pagados en efectivo."}
        </p>
        {!isAdmin && (
          <div className="mt-3 text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-md px-3 py-2 inline-block">
            Solo puedes registrar gastos pagados en <strong>efectivo</strong>. Los gastos por transferencia o tarjeta los registra el administrador.
          </div>
        )}
      </div>

      <Card className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Fecha</Label>
          <Input
            type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
            data-testid="gasto-fecha"
            className="mt-1.5 bg-blue-50 border-blue-200 text-blue-900"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Sucursal</Label>
          <Select value={sucursalId} onValueChange={setSucursalId}>
            <SelectTrigger data-testid="gasto-sucursal" className="mt-1.5 bg-blue-50 border-blue-200 text-blue-900">
              <SelectValue placeholder="Sucursal" />
            </SelectTrigger>
            <SelectContent>
              {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Capturado por</Label>
          <div className="mt-1.5 px-3 py-2 bg-slate-800 text-slate-100 rounded-md text-sm font-mono">{user?.email}</div>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        {filas.map((f, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid={`gasto-fila-${i}`}>
            <div className={isAdmin ? "md:col-span-3" : "md:col-span-3"}>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Categoría</Label>
              <Select value={f.categoria} onValueChange={(v) => setFila(i, "categoria", v)}>
                <SelectTrigger className="mt-1.5 bg-blue-50 border-blue-200 text-blue-900" data-testid={`gasto-cat-${i}`}>
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Importe</Label>
              <Input
                type="number" min="0" step="0.01" inputMode="decimal"
                value={f.importe} onChange={(e) => setFila(i, "importe", e.target.value)}
                data-testid={`gasto-imp-${i}`}
                className="mt-1.5 bg-blue-50 border-blue-200 text-blue-900 font-mono"
              />
            </div>
            {isAdmin && (
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Método</Label>
                <Select value={f.metodo_pago || "efectivo"} onValueChange={(v) => setFila(i, "metodo_pago", v)}>
                  <SelectTrigger className="mt-1.5 bg-blue-50 border-blue-200 text-blue-900" data-testid={`gasto-met-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={isAdmin ? "md:col-span-4" : "md:col-span-6"}>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Comentario</Label>
              <Input
                value={f.comentario} onChange={(e) => setFila(i, "comentario", e.target.value)}
                placeholder="Opcional"
                data-testid={`gasto-com-${i}`}
                className="mt-1.5 bg-blue-50 border-blue-200 text-blue-900"
              />
            </div>
            <div className="md:col-span-1">
              <Button
                variant="ghost" size="icon"
                onClick={() => removeFila(i)} disabled={filas.length === 1}
                data-testid={`gasto-del-${i}`}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
        <div className="flex justify-between items-center pt-3 border-t border-slate-200">
          <Button variant="outline" onClick={addFila} data-testid="gasto-add">
            <Plus className="w-4 h-4 mr-2" /> Agregar Gasto
          </Button>
          <div className="text-right">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total</div>
            <div className="font-mono text-2xl font-bold bg-slate-900 text-white px-4 py-1.5 rounded" data-testid="gasto-total">
              {fmtMoney(total)}
            </div>
          </div>
        </div>
      </Card>

      <div className="flex gap-3">
        <Button
          onClick={guardar} disabled={saving || !puedeEditar}
          data-testid="gasto-guardar"
          className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-8"
        >
          <Save className="w-4 h-4 mr-2" /> {!puedeEditar ? "Solo lectura" : saving ? "Guardando…" : "Guardar Gastos"}
        </Button>
      </div>
    </div>
  );
}
